import {
  Avatar,
  Button,
  Group,
  MantineColor,
  Modal,
  MultiSelect,
  SelectItemProps,
  Text,
} from "@mantine/core";
import React, { forwardRef, useState } from "react";

import ModalHeader from "components/ModalHeader";
import { Project } from "types";
import { useQueryClient } from "@tanstack/react-query";
import { useUser } from "hooks/useUser";
import { useGetOrg } from "api/organizations/getOrg";
import { getInitials } from "utils/getInitials";
import { ExclamationMark } from "tabler-icons-react";
import { showNotification } from "@mantine/notifications";
import {
  MemberPayload,
  useAddProjectMembers,
} from "api/projects/useAddProjectMembers";

type AddMemberModalProps = {
  opened: boolean;
  onClose: () => void;
  project: Project;
};

interface ItemProps extends SelectItemProps {
  color: MantineColor;
  description: string;
  image: string;
}

const AutoCompleteItem = forwardRef<HTMLDivElement, ItemProps>(
  ({ description, label, image, ...others }: ItemProps, ref) => (
    <div ref={ref} {...others}>
      <Group noWrap>
        <Avatar color="violet.7" radius="xl">
          {image}
        </Avatar>

        <div>
          <Text>{label}</Text>
        </div>
      </Group>
    </div>
  )
);

type MemberOption = {
  label: string;
  value: string;
  image: string;
};

function AddMemberModal({ opened, onClose, project }: AddMemberModalProps) {
  const queryClient = useQueryClient();
  const { user } = useUser();
  const { data: org } = useGetOrg(user.org?._id);

  const { mutate: addMembers, isLoading } = useAddProjectMembers();

  const data: MemberOption[] =
    org?.members.map((item) => ({
      label: `${item.firstName} ${item.lastName}`,
      value: item._id,
      image: getInitials(item.firstName, item.lastName),
    })) ?? [];
  const [value, setValue] = useState<string[]>([]);
  const handleSubmit = async () => {
    if (value.length === 0) {
      showNotification({
        title: "Warning",
        message: "Please select members first.",
        color: "yellow",
        icon: <ExclamationMark />,
      });
      return;
    }
    const payload: MemberPayload = {
      projectId: project?._id,
      memberIds: value,
    };

    addMembers(payload);
    await queryClient.invalidateQueries(["org", user.org?._id, "projects"]);

    onClose();
  };
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="xl"
      withCloseButton={false}
      transitionDuration={300}
      centered
      overlayOpacity={0.5}
    >
      <ModalHeader
        title={`Add Members to ${project?.name}`}
        onClose={onClose}
        ariaLabel="Close add members modal"
      />

      <MultiSelect
        value={value}
        onChange={setValue}
        label="Choose members"
        placeholder="Pick all you like"
        itemComponent={AutoCompleteItem}
        data={data}
        searchable
        nothingFound="Nobody here"
        maxDropdownHeight={500}
        filter={(v, selected, item: MemberOption) =>
          !selected && item.label.toLowerCase().includes(v.toLowerCase().trim())
        }
        clearButtonLabel="Clear selection"
        clearable
      />

      <Group position="right" spacing={20} mt={20}>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="filled"
          onClick={handleSubmit}
          disabled={isLoading}
        >
          Update
        </Button>
      </Group>
    </Modal>
  );
}

export default AddMemberModal;
